import { useMutation } from "@apollo/client/react";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { Form, Formik } from "formik";
import { object, string } from "yup";
import type { ErrorLike } from "@apollo/client/core";
import { gql } from "graphql-tag";
import { RequestCodeViaEmailDocument } from "./generated";
import { useCallback } from "react";
import { captureException } from "@sentry/nextjs";
import {
  useEmailTypoWarning,
  EMAIL_MISSPELLED_ERROR,
} from "@portal/app/_components/hooks/useEmailTypoWarning";
import { EmailTypoWarning } from "@portal/app/_components/ui-elements/EmailTypoWarning";
import { ClientErrorMessage } from "@portal/app/_common/lib/clientErrorUtils";
import { ValidatedFormikField } from "@portal/app/_components/ui-elements/formHelpers";
import { FormInputText } from "@portal/app/_components/ui-elements/FormInputs";
import { LOCAL_STORAGE_KEYS, useLocalStorage } from "@portal/app/_components/hooks/useLocalStorage";
import Box from "@mui/material/Box";
import { logger } from "@portal/app/_common/lib/logger";
import { SIGN_IN_METHOD, useSignInContext } from "@portal/app/_components/flow/steps/SignInStep";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/pro-solid-svg-icons/faSpinner";

void gql`
  mutation requestCodeViaEmail($email: Email!, $redirect: String, $additionalParams: String) {
    requestCodeViaEmail(email: $email, redirect: $redirect, additionalParams: $additionalParams)
  }
`;

const DEFAULT_USER_EMAIL_ERROR: ClientErrorMessage = {
  title: "That email didn't work",
  description: "We didn't find an account with that email. Please check for typos and try again.",
};
const EmailInput = ({ loading }: { loading: boolean }) => {
  const { onBlur, isMisspelled, warningBorderStyle } = useEmailTypoWarning();
  return (
    <>
      <Box sx={{ mt: 2 }}>
        <ValidatedFormikField
          as={FormInputText}
          name="email"
          type="email"
          placeholder="Enter your email address"
          disabled={loading}
          data-hj-allow={true}
          autoFocus
          onBlur={onBlur}
          style={warningBorderStyle}
        />
      </Box>
      {isMisspelled && (
        <Box sx={{ mt: 1 }}>
          <EmailTypoWarning />
        </Box>
      )}
    </>
  );
};

type FormValues = { email: string };
export const Email = () => {
  const { setPage, setClientErrorMessage } = useSignInContext();
  const [requestCodeViaEmail, { loading }] = useMutation(RequestCodeViaEmailDocument);
  const [, setMethod, removeMethod] = useLocalStorage<string>(LOCAL_STORAGE_KEYS.MAGIC_LINK_METHOD);
  const [, setEmail, removeEmail] = useLocalStorage<string>(LOCAL_STORAGE_KEYS.MAGIC_LINK_EMAIL);
  const [, , removePhone] = useLocalStorage<string>(LOCAL_STORAGE_KEYS.MAGIC_LINK_PHONE);
  const [, setCodeSent] = useLocalStorage<boolean>(LOCAL_STORAGE_KEYS.MAGIC_LINK_CODE_SENT);

  const onSubmit = useCallback(
    ({ email }: FormValues) => {
      setEmail(email);
      setMethod(SIGN_IN_METHOD.EMAIL);
      removePhone();

      if (email.endsWith("@att.net")) {
        setClientErrorMessage({
          title: "For AT&T emails, we recommend texting your phone number instead",
          severity: "warning",
          description: (
            <Typography paragraph>
              AT&amp;T is experiencing email delivery problems from many services, including
              Dreambound.
              <div>
                We recommend&nbsp;
                <Link
                  component="button"
                  onClick={() => {
                    setClientErrorMessage(null);
                    setPage("SELECT_METHOD");
                  }}
                >
                  using your phone number
                </Link>
                &nbsp;to sign in.
              </div>
            </Typography>
          ),
        });
        return;
      } else {
        setClientErrorMessage(null);
      }

      return requestCodeViaEmail({
        variables: {
          email,
          redirect: window.location.pathname,
          additionalParams: window.location.search.replace(/\?/g, "&"),
        },
      })
        .then(() => {
          setCodeSent(true);
          setPage("CODE");
        })
        .catch((error: ErrorLike) => {
          setClientErrorMessage(DEFAULT_USER_EMAIL_ERROR);
          captureException(error);
          logger.error(error);
          removeMethod();
          removeEmail();
        });
    },
    [
      setEmail,
      setMethod,
      removePhone,
      requestCodeViaEmail,
      setClientErrorMessage,
      setPage,
      setCodeSent,
      removeMethod,
      removeEmail,
    ]
  );

  return (
    <Formik<FormValues>
      initialValues={{ email: "" }}
      onSubmit={(values, { resetForm }) => {
        resetForm();
        return onSubmit(values);
      }}
      validationSchema={object().shape({
        email: string()
          .required("Enter your email address")
          .email("You must enter a valid email address"),
      })}
    >
      {({ errors }) => (
        <Form noValidate data-testid="email-form">
          <EmailInput loading={loading} />
          <Button
            type="submit"
            data-testid="submit"
            fullWidth
            sx={{ mt: 2 }}
            disabled={loading || (!!errors.email && errors.email !== EMAIL_MISSPELLED_ERROR)}
          >
            {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : "Continue"}
          </Button>
        </Form>
      )}
    </Formik>
  );
};
